import type { Prop } from '../content/types';
import type { Registry } from '../content/registry';
import type { Road, Terrain } from '../content/types';
import { CanvasPainter } from '../draw/canvas-painter';
import type { PlacedBuilding } from '../world/buildings';
import type { Lot } from '../world/lots';
import { CHUNK, WorldGen, type Chunk, type PlacedProp, type WorldGenOptions } from '../world/generator';
import { renderChunkGroundSteps, renderRoofFlat } from '../world/render-static';
import type { WireChunk } from '../world/gen.worker';
import { cellAt, cellWalkable, clearPlans, floorPlan, setFurniture, wallSlabs, CELL_DOOR, CELL_FURN, CELL_WALL, type FloorPlan } from '../world/interior';
import { Overlay, PIECE, type OverlayData } from '../world/overlay';
import { syncCustoms } from '../world/custom';

export const GROUND_PX = 16; // px per tile in the cached ground layer

/** Minimal worker shape shared by browser Workers and a node worker_threads adapter. */
/** (Oct 1) generator workers no world holds now, kept loaded for the next one (a worker made by a caller's factory is not pooled) */
const pooled: WorkerLike[] = [];
let tickets = 0;
export interface WorkerLike { postMessage(m: unknown, transfer?: Transferable[]): void; onmessage: ((e: { data: WireChunk }) => void) | null; onerror: ((e: unknown) => void) | null; terminate?(): void }

export interface LiveChunk {
  chunk: Chunk;
  ground?: HTMLCanvasElement | OffscreenCanvas; /** (Sep 12) the same at a quarter of the size, for the zoomed-out view */ groundLow?: HTMLCanvasElement | OffscreenCanvas;
  /** the neighbours that were not live when the ground was painted (bit (dy+1)*3+(dx+1)), so their arrival can mark it stale */ groundMissing?: number;
  /** painted before a neighbour arrived: still drawn, repainted by prepaint when it has a frame to spare */ groundStale?: boolean;
  /** (perf, Sep 17) the minimap's picture of this chunk, 64×64, one pixel a tile; cleared with the ground */
  mini?: HTMLCanvasElement;
  lastUsed: number;
  /** hook for the game: entities spawned for this chunk */
  spawned: boolean;
}

/**
 * Keeps generated chunks around the camera, renders their ground layer once, and
 * answers world queries (terrain, road, blocking height) at any tile.
 */
/** the tallest thing a vehicle pushes over (metres): the desert's cacti and the rocks are 2, the smallest trees 4 */
export const CRUSH_H = 2.5;

export class WorldStream {
  readonly gen: WorldGen;
  private live = new Map<string, LiveChunk>();
  private pending = new Set<string>();
  private workers: WorkerLike[] = [];
  private nextWorker = 0;
  private ownPool = false;
  private frame = 0;
  maxLive = 40;
  maxPending = 6; // (two workers keep three chunks each in flight: a plane at speed needs a column of chunks a second)
  /** simulation clock (seconds), used for traffic lights */
  clock = 0;
  /** the hour of the day (set with the clock): the traffic lights switch to flashing amber at night */
  hour = 12;
  /** rain 0..1 (weather.ts): roads are slippery — physics brakes and steers worse */
  wet = 0;
  /** How wet the ground is here: the world's rain, but a dry biome only feels the heaviest showers (as the sky shows it). */
  wetAt(x: number, y: number): number {
    if (this.wet <= 0) return 0;
    const precip = this.gen.fields.biomeAt(x, y).look?.precipitation;
    return precip === 'dry' ? Math.max(0, this.wet * 2 - 1) : this.wet;
  }
  onChunk?: (lc: LiveChunk) => void;

  /** (Oct 1) which world a worker's answer is for (see the pool below) */
  private readonly ticket = ++tickets;
  constructor(seed: number, readonly reg: Registry, workers = 0, factory?: () => WorkerLike, opts: WorldGenOptions = {}) {
    this.gen = new WorldGen(seed, reg, opts);
    setFurniture(reg.all('prop').filter((p) => p.placement === 'indoor').map((p) => ({ id: p.id, height: p.height, rooms: p.rooms ?? [], indoor: p.indoor ?? 'wall', density: p.density, ...(p.tags?.includes('store') ? { store: true } : {}) })));
    for (let i = 0; i < workers; i++) {
      try {
        const w: WorkerLike = factory ? factory() : pooled.pop() ?? (new Worker(new URL('../world/gen.worker.ts', import.meta.url), { type: 'module' }) as unknown as WorkerLike);
        if (!factory) this.ownPool = true;
        w.postMessage({ type: 'init', seed, paths: !!opts.paths, smooth: !!opts.smooth, ticket: this.ticket });
        w.onmessage = (e) => this.receive(e.data);
        w.onerror = (e) => { console.warn('gen worker failed, falling back to sync generation', e); this.workers = []; this.pending.clear(); this.gen.places.learnOnly = false; };
        this.workers.push(w);
      } catch { /* no worker support: sync */ }
    }
    this.gen.places.learnOnly = this.workers.length > 0;
  }

  static key(cx: number, cy: number): string { return `${cx},${cy}`; }

  /** Stop background workers and drop chunks (when this world is replaced). */
  dispose(): void {
    // (Oct 1, owner: "loading … done on the landing page") the workers of the world behind the landing card go on to the joined
    // one: a new worker fetched and parsed the whole content pack again before its first chunk — seconds of an empty city on join
    for (const w of this.workers) { if (this.ownPool) { w.onmessage = null; w.onerror = null; pooled.push(w); } else w.terminate?.(); }
    this.workers = [];
    this.pending.clear();
    this.live.clear(); this.liveGen++;
  }

  private receive(w: WireChunk): void {
    if (w.ticket !== undefined && w.ticket !== this.ticket) return; // (asked for by the world this worker served before)
    const k = WorldStream.key(w.cx, w.cy);
    this.pending.delete(k);
    if (w.places) this.gen.places.learn(w.places); // (perf, Sep 17: the worker probed; this thread only remembers)
    if (this.live.has(k)) return;
    const chunk: Chunk = {
      cx: w.cx, cy: w.cy, x0: w.x0, y0: w.y0, terrain: w.terrain, road: w.road, roadFlags: w.roadFlags, zone: w.zone, blockH: w.blockH, decorH: w.decorH, height: w.height ?? new Float32Array(CHUNK * CHUNK), lit: w.lit ?? new Int8Array(CHUNK * CHUNK), step: w.step ?? new Uint8Array(CHUNK * CHUNK), deckH: w.deckH, deckRoad: w.deckRoad, deckFlags: w.deckFlags,
      buildings: w.buildings.map((b) => ({ ...b, def: this.reg.need('building', b.id) })), sites: (w.sites ?? []).map((b) => ({ ...b, def: this.reg.need('building', b.id) })), parking: w.parking ?? [], lots: w.lots ?? [], decor: w.decor, props: w.props, boards: w.boards ?? [],
    };
    this.adopt(chunk);
  }

  // ---- the player-grown layer (world/overlay.ts) ----
  get overlay(): Overlay { return this.gen.fields.overlay; }
  private overlaySent = -1;
  /** Push the overlay to the generator workers when it changed (before they generate anything with it). */
  private syncOverlay(): void {
    const ov = this.overlay;
    if (ov.version === this.overlaySent) return;
    this.overlaySent = ov.version;
    const d = ov.toJSON();
    for (const w of this.workers) w.postMessage({ type: 'overlay', d });
  }
  /** Drop live/pending chunks touching a world rect so they regenerate with the current overlay. */
  invalidateRect(x0: number, y0: number, x1: number, y1: number): number {
    this.syncOverlay();
    let n = 0;
    const cx0 = Math.floor(x0 / CHUNK), cy0 = Math.floor(y0 / CHUNK), cx1 = Math.floor((x1 - 1) / CHUNK), cy1 = Math.floor((y1 - 1) / CHUNK);
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
      const k = WorldStream.key(cx, cy);
      if (this.dropLive(k)) n++;
      this.pending.delete(k);
    }
    return n;
  }
  /** Something in the overlay changed inside a rect (a piece finished, a cell zoned, a site done): regenerate what it can touch (lots reach 72 tiles from a road). */
  overlayChanged(x0: number, y0: number, x1: number, y1: number, regen = true): void {
    this.syncOverlay();
    if (regen) this.invalidateRect(...Overlay.reach(x0, y0, x1, y1));
  }
  /** A plant needs no regeneration: put it straight into the live chunk. */
  applyPlant(x: number, y: number, propId: string): void {
    const lc = this.live.get(WorldStream.key(Math.floor(x / CHUNK), Math.floor(y / CHUNK))); if (!lc) return;
    const c = lc.chunk, i = (y - c.y0) * CHUNK + (x - c.x0);
    const pi = this.gen.propList.findIndex((q) => q.id === propId); if (pi < 0) return;
    c.decor = c.decor.filter((d) => d.x !== x || d.y !== y); c.props = c.props.filter((q) => q.x !== x || q.y !== y);
    c.props.push({ x, y, prop: pi });
    const h = this.gen.propList[pi].height; c.blockH[i] = h; c.decorH[i] = h;
    this.unpaint(lc);
  }
  /**
   * Replace the overlay with the server's copy (clients): whatever changed in a way the generator cares
   * about (a piece finished or removed, a cell zoned, a site started or finished) regenerates around it;
   * plans and paving progress only change what the renderer draws.
   */
  setOverlay(d: OverlayData): void {
    const ov = this.overlay;
    const was = { roads: new Map(ov.roads), zones: new Map(ov.zones), sites: new Map(ov.sites), plants: new Map(ov.plants), customs: new Map([...ov.customs].map(([k, v]) => [k, JSON.stringify(v)])), plots: new Map([...ov.plots].map(([k, v]) => [k, `${v.owner}|${v.built ?? ''}`])) };
    ov.load(d);
    syncCustoms(this.reg, ov); // (owner, Sep 22) the players' designs answer by id
    const rects: [number, number, number, number][] = [];
    const pieceRect = Overlay.pieceRect; // (Sep 18: the gaps a piece paves to the kerb are part of what changed)
    for (const [k, r] of ov.roads) { const old = was.roads.get(k); if ((old?.b ?? 0) >= PIECE !== r.b >= PIECE) rects.push(pieceRect(r)); }
    for (const [k, r] of was.roads) if (!ov.roads.has(k) && r.b >= PIECE) rects.push(pieceRect(r));
    for (const [k, z] of ov.zones) if (was.zones.get(k)?.z !== z.z) rects.push([z.x * PIECE, z.y * PIECE, z.x * PIECE + PIECE, z.y * PIECE + PIECE]);
    for (const [k, z] of was.zones) if (!ov.zones.has(k)) rects.push([z.x * PIECE, z.y * PIECE, z.x * PIECE + PIECE, z.y * PIECE + PIECE]);
    const stage = (p: number | undefined) => (p === undefined || p <= 0 ? 0 : p >= 1 ? 2 : 1);
    for (const [k, p] of ov.sites) if (stage(was.sites.get(k)) !== stage(p)) { const site = this.siteByKey(k); if (site) rects.push([site.x, site.y, site.x + site.w, site.y + site.h]); else rects.push(this.lotRectOf(k)); }
    for (const k of was.sites.keys()) if (!ov.sites.has(k)) rects.push(this.lotRectOf(k)); // (a site cleared: a demolition)
    for (const [k, pl] of ov.plants) if (!was.plants.has(k)) this.applyPlant(pl.x, pl.y, pl.p);
    for (const k of new Set([...was.customs.keys(), ...ov.customs.keys()])) if (was.customs.get(k) !== (ov.customs.has(k) ? JSON.stringify(ov.customs.get(k)) : undefined)) { rects.push(this.lotRectOf(k)); clearPlans({ key: k } as PlacedBuilding); clearPlans({ key: `${k}#1` } as PlacedBuilding); clearPlans({ key: `${k}#2` } as PlacedBuilding); } // (a design placed or changed: the lot regenerates, the plans are drawn again for the new storeys)
    for (const k of new Set([...was.plots.keys(), ...ov.plots.keys()])) if (was.plots.get(k) !== (ov.plots.has(k) ? `${ov.plots.get(k)!.owner}|${ov.plots.get(k)!.built ?? ''}` : undefined)) rects.push(this.lotRectOf(k)); // (a plot bought or a house demolished: the generator's pick goes, the lot is bare)
    this.syncOverlay();
    for (const [x0, y0, x1, y1] of rects) this.invalidateRect(...Overlay.reach(x0, y0, x1, y1, 4));
  }
  /** The site (unbuilt planned building) with this lot key, in live chunks. */
  siteByKey(key: string): PlacedBuilding | undefined { for (const lc of this.live.values()) { const s = lc.chunk.sites.find((b) => b.key === key); if (s) return s; } return undefined; }
  /** (owner, Sep 22) a lot of a live chunk by key (the generated towns' too: their keys name a path edge, not a lattice strip) */
  lotByKey(key: string): Lot | undefined { for (const lc of this.live.values()) { const l = lc.chunk.lots.find((q) => q.key === key); if (l) return l; } return undefined; }
  /** vacant lots — plots for sale — in live chunks whose rect touches a world rect (the renderer's signs, the map's search) */
  vacantLotsIn(x0: number, y0: number, x1: number, y1: number): Lot[] {
    const out: Lot[] = [];
    for (const lc of this.live.values()) { const c = lc.chunk; if (c.x0 >= x1 || c.x0 + CHUNK <= x0 || c.y0 >= y1 || c.y0 + CHUNK <= y0) continue; for (const l of c.lots) if (l.vacant && l.x < x1 && l.x + l.w > x0 && l.y < y1 && l.y + l.h > y0 && !out.some((q) => q.key === l.key)) out.push(l); }
    return out;
  }
  private lotRectOf(key: string): [number, number, number, number] { const l = this.lotByKey(key); return l ? [l.x, l.y, l.x + l.w, l.y + l.h] : this.lotRect(key); }
  /** Where a planned lot lies, from its key alone (`plot:axis:pos:from:i:side:n` → the piece's strip; good enough to find the chunk). */
  private lotRect(key: string): [number, number, number, number] {
    const m = /^plot:([vh]):(-?\d+):(-?\d+):/.exec(key);
    if (!m) return [0, 0, 0, 0];
    const p = Number(m[2]), f = Number(m[3]);
    return m[1] === 'v' ? [p - 40, f, p + 40, f + PIECE] : [f, p - 40, f + PIECE, p + 40];
  }
  /** Business parking lots in live chunks whose rect touches a world rect. */
  parkingIn(x0: number, y0: number, x1: number, y1: number): Chunk['parking'] {
    const out: Chunk['parking'] = [];
    for (const lc of this.live.values()) { const c = lc.chunk; if (c.x0 >= x1 || c.x0 + CHUNK <= x0 || c.y0 >= y1 || c.y0 + CHUNK <= y0) continue; for (const p of c.parking) if (p.x < x1 && p.x + p.w > x0 && p.y < y1 && p.y + p.h > y0 && !out.some((q) => q.key === p.key)) out.push(p); }
    return out;
  }
  /** Construction sites in live chunks (for jobs and the renderer). */
  sitesIn(x0: number, y0: number, x1: number, y1: number): PlacedBuilding[] {
    const out: PlacedBuilding[] = [];
    for (const lc of this.live.values()) for (const s of lc.chunk.sites) if (s.x < x1 && s.x + s.w > x0 && s.y < y1 && s.y + s.h > y0) out.push(s);
    return out;
  }

  private adopt(chunk: Chunk): LiveChunk {
    const lc: LiveChunk = { chunk, lastUsed: this.frame, spawned: false };
    this.live.set(WorldStream.key(chunk.cx, chunk.cy), lc); this.liveGen++;
    // (the overnight review, Sep 12) a neighbour painted its apron from the fields while this chunk was still pending:
    // its seam edges are guesses. Mark it, and prepaint redoes it when there is nothing new to paint.
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const nb = this.live.get(WorldStream.key(chunk.cx + dx, chunk.cy + dy));
      if (nb?.ground && nb.groundMissing && nb.groundMissing & (1 << ((1 - dy) * 3 + (1 - dx)))) nb.groundStale = true;
    }
    if (this.broken.size) this.applyBroken(lc);
    if (this.ruins.size) for (const k of this.ruins.keys()) { const [x, y] = k.split(',').map(Number); this.applyRuin(lc, x, y); }
    this.onChunk?.(lc);
    return lc;
  }

  /** Get a live chunk. With create=true generates synchronously on this thread (startup only). */
  get(cx: number, cy: number, create = true): LiveChunk | undefined {
    const k = WorldStream.key(cx, cy);
    let lc = this.live.get(k);
    if (!lc && create) lc = this.adopt(this.gen.generate(cx, cy));
    if (lc) lc.lastUsed = this.frame;
    return lc;
  }

  private request(cx: number, cy: number): void {
    if (this.workers.length === 0) { this.get(cx, cy); return; }
    if (this.overlay.version !== this.overlaySent) this.syncOverlay();
    const k = WorldStream.key(cx, cy);
    if (this.pending.has(k) || this.pending.size >= this.maxPending) return;
    this.pending.add(k);
    const w = this.workers[this.nextWorker++ % this.workers.length];
    w.postMessage({ type: 'gen', cx, cy });
  }

  /** A look at a chunk for a search (the phone's Fuel app): the live chunk if it is in, else asks the workers for it (no thread
   *  generation — without workers it generates now) and answers undefined until it lands. Keeps it warm so the search's ring is not evicted first. */
  probe(cx: number, cy: number): LiveChunk | undefined {
    const lc = this.live.get(WorldStream.key(cx, cy));
    if (lc) { lc.lastUsed = this.frame; return lc; }
    this.request(cx, cy);
    return this.live.get(WorldStream.key(cx, cy));
  }

  /** Ensure chunks covering a world rect exist; request at most `budget` new ones per call (async when workers exist). */
  ensure(x0: number, y0: number, x1: number, y1: number, budget = 2, sync = false): void {
    this.frame++;
    const cx0 = Math.floor(x0 / CHUNK), cy0 = Math.floor(y0 / CHUNK), cx1 = Math.floor(x1 / CHUNK), cy1 = Math.floor(y1 / CHUNK);
    // nearest first
    const todo: [number, number, number][] = [];
    const mx = (cx0 + cx1) / 2, my = (cy0 + cy1) / 2;
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
      const k = WorldStream.key(cx, cy);
      const lc = this.live.get(k);
      if (lc) lc.lastUsed = this.frame; else if (!this.pending.has(k)) todo.push([(cx - mx) ** 2 + (cy - my) ** 2, cx, cy]);
    }
    todo.sort((a, b) => a[0] - b[0]);
    for (const [, cx, cy] of todo.slice(0, budget)) { if (sync) this.get(cx, cy); else this.request(cx, cy); }
    if (this.live.size > this.maxLive) {
      const arr = [...this.live.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
      for (const [k, lc] of arr.slice(0, this.live.size - this.maxLive)) { this.dropLive(k); this.forgetChanges(lc.chunk); }
    }
  }

  get pendingCount(): number { return this.pending.size; }
  /** (Oct 1) how much of a rect is in and painted, 0..1 — the join's loading bar waits for the ground round the player */
  readiness(x0: number, y0: number, x1: number, y1: number): number {
    const cx0 = Math.floor(x0 / CHUNK), cy0 = Math.floor(y0 / CHUNK), cx1 = Math.floor(x1 / CHUNK), cy1 = Math.floor(y1 / CHUNK);
    let n = 0, done = 0;
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) { n += 2; const lc = this.live.get(WorldStream.key(cx, cy)); if (lc) done += lc.ground ? 2 : 1; }
    return n ? done / n : 1;
  }
  /** moves whenever a chunk comes or goes: a per-frame scan of the live chunks can skip a frame where it has not moved */
  get generation(): number { return this.liveGen; }

  liveChunks(): IterableIterator<LiveChunk> { return this.live.values(); }

  /**
   * (Sep 11, the freeze hunt) Paint the ground of one chunk that is live but not yet painted, nearest to the view first,
   * within a margin round it — so a chunk's ground and its first draw (the texture upload) happen before it scrolls into
   * view, not all at once when it does. Returns a ground it finished this frame, for the caller to draw once (the upload).
   * (owner, Sep 25: "when driving fast … a noticeable jerking") a whole chunk was 10–40 ms in one frame, four a second at
   * 40 m/s — a missed frame or three each on a 144 Hz screen. Now it paints a slice at a time (`renderChunkGroundSteps`)
   * for at most `budget` ms a frame — the renderer passes what the frame has to spare — and the next chunk is the one
   * nearest a point `aheadX, aheadY` tiles ahead of the view, where the camera is going.
   */
  prepaint(x0: number, y0: number, x1: number, y1: number, budget = 1e9, aheadX = 0, aheadY = 0): HTMLCanvasElement | OffscreenCanvas | undefined {
    if (budget <= 0) return undefined;
    const t0 = performance.now();
    for (;;) {
      if (!this.job) {
        const cx0 = Math.floor(x0 / CHUNK) - 1, cy0 = Math.floor(y0 / CHUNK) - 1, cx1 = Math.floor(x1 / CHUNK) + 1, cy1 = Math.floor(y1 / CHUNK) + 1;
        const mx = (cx0 + cx1) / 2 + aheadX / CHUNK, my = (cy0 + cy1) / 2 + aheadY / CHUNK;
        let best: LiveChunk | undefined, bd = Infinity, stale: LiveChunk | undefined, sd = Infinity;
        for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
          const lc = this.live.get(WorldStream.key(cx, cy));
          if (!lc) continue;
          const d = (cx - mx) ** 2 + (cy - my) ** 2;
          if (!lc.ground) { if (d < bd) { bd = d; best = lc; } } else if (lc.groundStale && d < sd) { sd = d; stale = lc; }
        }
        const lc = best ?? stale; // (unpainted first; a stale seam can wait — it keeps its old picture until the new one is done)
        if (!lc) return undefined;
        this.job = this.startPaint(lc);
      }
      const job = this.job;
      if (job.it.next().done) { this.job = undefined; return this.commitPaint(job); } // (one a frame: its first draw is the upload)
      if (performance.now() - t0 >= budget) return undefined;
    }
  }
  /** (owner, Sep 25) pixels a tile in the cached ground (the graphics quality: 16, 12 or 8); a change repaints every ground */
  get groundPx(): number { return this.gpx; }
  set groundPx(px: number) {
    if (px === this.gpx) return;
    this.gpx = px; this.canvasPool.length = 0;
    if (this.job) { this.job.it.return(); this.job = undefined; }
    // (Oct 2) the old pictures stay on screen until the new ones are painted, a slice at a time — dropping them all repainted every chunk
    // in view in one frame (46 paints, 349 ms, a 242 ms frame at each of Auto's steps)
    for (const lc of this.live.values()) if (lc.ground) lc.groundStale = true;
  }
  private gpx = GROUND_PX;
  /** the chunk ground being painted a slice at a time (`prepaint`) */
  private job?: { lc: LiveChunk; it: Generator<void, void, void>; cv: HTMLCanvasElement | OffscreenCanvas; missing: number };
  /**
   * (Sep 25: the jerking at speed) the grounds of dropped chunks, kept for the next ones to paint on: a 1024² canvas made and dropped
   * every few seconds at speed left the collector a GPU surface to free each time (its pauses reached 65 ms, 50 of them in the
   * embedder's epilogue — a Chrome trace). A few are reused instead.
   */
  private canvasPool: (HTMLCanvasElement | OffscreenCanvas)[] = [];
  private recycle(cv: HTMLCanvasElement | OffscreenCanvas | undefined): void { if (cv && cv.width === CHUNK * this.gpx && this.canvasPool.length < 8 && !this.canvasPool.includes(cv)) this.canvasPool.push(cv); } // (a canvas of the old resolution is not reused)
  private startPaint(lc: LiveChunk): NonNullable<WorldStream['job']> {
    const px = this.gpx, size = CHUNK * px, old = this.canvasPool.pop();
    const cv = old ?? (typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(size, size) : Object.assign(document.createElement('canvas'), { width: size, height: size }));
    const ctx = cv.getContext('2d') as CanvasRenderingContext2D;
    if (old) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.clearRect(0, 0, size, size); }
    const p = new CanvasPainter(ctx), c = lc.chunk, broken = this.broken;
    const it = (function* (w: WorldStream) {
      yield* renderChunkGroundSteps(p, w.gen, c, 0, 0, px, broken, (cx, cy) => w.live.get(WorldStream.key(cx, cy))?.chunk);
      for (const b of c.buildings) if (b.height <= 0) renderRoofFlat(p, b, -c.x0 * px, -c.y0 * px, px);
    })(this);
    let missing = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if ((dx || dy) && !this.live.has(WorldStream.key(c.cx + dx, c.cy + dy))) missing |= 1 << ((dy + 1) * 3 + (dx + 1));
    return { lc, it, cv, missing };
  }
  private commitPaint(job: NonNullable<WorldStream['job']>): HTMLCanvasElement | OffscreenCanvas | undefined {
    const lc = job.lc, c = lc.chunk;
    if (this.live.get(WorldStream.key(c.cx, c.cy)) !== lc) { this.recycle(job.cv); return undefined; } // (dropped or regenerated meanwhile)
    if (lc.ground !== job.cv) this.recycle(lc.ground); // (a stale picture, repainted)
    lc.ground = job.cv; lc.groundLow = undefined; lc.groundStale = false; lc.groundMissing = job.missing;
    // a neighbour that arrived while the slices were painting found no ground here to mark stale: mark it now
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if ((dx || dy) && job.missing & (1 << ((dy + 1) * 3 + (dx + 1))) && this.live.has(WorldStream.key(c.cx + dx, c.cy + dy))) lc.groundStale = true;
    return job.cv;
  }
  /** a chunk's pictures are out of date: dropped, and a paint of it in progress abandoned */
  private unpaint(lc: LiveChunk): void {
    this.recycle(lc.ground);
    lc.ground = undefined; lc.groundLow = undefined; lc.mini = undefined;
    if (this.job?.lc === lc) { this.job.it.return(); this.job = undefined; } // (its canvas is left to the collector: its drawing state is mid-paint)
  }
  /**
   * (Sep 12) the ground at a quarter of the size, for the zoomed-out view: drawn once from the full cache and kept beside
   * it. Forty chunks of 1024² sampled down to a few pixels each were the whole GPU budget when flying high.
   */
  groundLow(lc: LiveChunk): HTMLCanvasElement | OffscreenCanvas {
    if (lc.groundLow && lc.ground) return lc.groundLow;
    const full = this.ground(lc), size = (CHUNK * this.gpx) / 4;
    const cv = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(size, size) : Object.assign(document.createElement('canvas'), { width: size, height: size });
    const ctx = cv.getContext('2d') as CanvasRenderingContext2D;
    ctx.drawImage(full as CanvasImageSource, 0, 0, size, size);
    lc.groundLow = cv;
    return cv;
  }
  /** A chunk's ground, painted now if it has none (a chunk on screen that `prepaint` did not reach in time). */
  ground(lc: LiveChunk): HTMLCanvasElement | OffscreenCanvas {
    if (lc.ground) return lc.ground;
    // one paint at a time (the terrain's scratch grids are shared): this chunk's own slices are finished, another's abandoned
    let job = this.job;
    if (job && job.lc !== lc) { job.it.return(); job = undefined; }
    job ??= this.startPaint(lc);
    this.job = undefined;
    while (!job.it.next().done);
    this.commitPaint(job);
    return job.cv;
  }

  // ---- queries (tile coords may be fractional; floors them) ----
  // (Sep 11, the freeze hunt) every tile query built a "cx,cy" string and looked it up: 5% of a fast drive. Consecutive
  // queries are nearly always in the same chunk, so one remembered chunk answers most of them; `liveGen` moves whenever the
  // live set changes, which is what makes the remembered one safe to trust
  private cacheCx = Number.NaN; private cacheCy = Number.NaN; private cacheLc: LiveChunk | undefined; private cacheGen = -1; private liveGen = 0;
  private chunkAt(x: number, y: number): Chunk | undefined {
    const cx = Math.floor(Math.floor(x) / CHUNK), cy = Math.floor(Math.floor(y) / CHUNK);
    if (cx === this.cacheCx && cy === this.cacheCy && this.cacheGen === this.liveGen) { const lc = this.cacheLc; if (lc) lc.lastUsed = this.frame; return lc?.chunk; }
    const lc = this.get(cx, cy, false);
    this.cacheCx = cx; this.cacheCy = cy; this.cacheLc = lc; this.cacheGen = this.liveGen;
    return lc?.chunk;
  }
  private dropLive(k: string): boolean { const lc = this.live.get(k); if (lc) this.unpaint(lc); this.liveGen++; return this.live.delete(k); }
  private idx(c: Chunk, x: number, y: number): number { return (Math.floor(y) - c.y0) * CHUNK + (Math.floor(x) - c.x0); }

  /** (track 2) the ground's height in metres at a tile (0 where no chunk is loaded yet) */
  heightAt(x: number, y: number): number {
    const c = this.chunkAt(x, y); if (!c || !c.height) return 0;
    return c.height[(Math.floor(y) - c.y0) * CHUNK + (Math.floor(x) - c.x0)];
  }
  /** (track 2) metres of climb per metre travelled along a heading — what a car feels: positive = uphill ahead */
  gradeAlong(x: number, y: number, angle: number): number {
    const dx = Math.sin(angle) * 2, dy = -Math.cos(angle) * 2;
    return (this.heightAt(x + dx, y + dy) - this.heightAt(x - dx, y - dy)) / 4;
  }
  terrainAt(x: number, y: number): Terrain | undefined {
    const c = this.chunkAt(x, y); if (!c) return undefined;
    return this.gen.terrainList[c.terrain[this.idx(c, x, y)]];
  }
  /**
   * Overpasses: a tile can carry a deck (highway) above the ground layer. Road queries answer for
   * the layer the *current mover* is on: set `moverAlt` (metres) before stepping an entity; a mover
   * within DECK_REACH below a deck is on it (that is how a car at ground level rolls onto a ramp).
   */
  moverAlt = 0;
  /** the current mover stands on a deck right now (set with moverAlt): the deck's edges are a railing for it */
  moverOnDeck = false;
  static readonly DECK_REACH = 1.0;
  /** set both mover fields from a position + altitude (call before stepping an entity; reset with setMover(0, 0, 0)) */
  setMover(x: number, y: number, alt: number): void { this.moverAlt = Math.max(0, alt); const dh = this.deckH(x, y); this.moverOnDeck = dh > 0 && this.moverAlt >= dh - WorldStream.DECK_REACH; }
  /** deck height at a tile (0 = none) */
  deckH(x: number, y: number): number {
    const c = this.chunkAt(x, y); if (!c) return 0;
    return c.deckH[this.idx(c, x, y)];
  }
  /** is the current mover on the deck of this tile? */
  onDeck(x: number, y: number): boolean {
    const dh = this.deckH(x, y);
    return dh > 0 && this.moverAlt >= dh - WorldStream.DECK_REACH;
  }
  /** `lift`: as blockH's — a probe at a vehicle's end on a ramp is above its centre, and still on the deck's road */
  roadAt(x: number, y: number, lift = 0): Road | undefined {
    const c = this.chunkAt(x, y); if (!c) return undefined;
    const j = this.idx(c, x, y);
    const i = c.deckH[j] > 0 && this.moverAlt + lift >= c.deckH[j] - WorldStream.DECK_REACH ? c.deckRoad[j] : c.road[j];
    return i ? this.gen.roadList[i - 1] : undefined;
  }
  /** Height of whatever blocks this tile (0 = nothing). Unloaded = 0. On a deck: the deck is clear, its edges are a drop (railing). */
  /**
   * `lift`: how much higher than the mover's centre this point may be and still count as on the deck — a vehicle's
   * corner on a ramp is above its centre by the grade times its half-length, and a long vehicle at the foot of a ramp
   * would otherwise see the ramp as a wall (physics.ts `blockedPoints`)
   */
  blockH(x: number, y: number, lift = 0): number {
    const c = this.chunkAt(x, y); if (!c) return 0;
    const j = this.idx(c, x, y);
    const dh = c.deckH[j];
    if (dh > 0 && this.moverAlt + lift >= dh - WorldStream.DECK_REACH) return 0; // on (or rolling onto) the deck: it is clear
    // off the edge: the railing holds (a raised mover never leaves the deck sideways) — less the probe's own allowance:
    // on a ramp the tail of a long vehicle is *below* its centre by the grade times its half-length, and the railing read a
    // bus's own tail as over the edge, so every bus and truck stopped dead a few tiles up a bridge ramp (bug report, Sep 12)
    if (this.moverOnDeck && dh < this.moverAlt - 1.5 - lift) return 99;
    return c.blockH[j];
  }
  roadFlags(x: number, y: number): number {
    const c = this.chunkAt(x, y); if (!c) return 0;
    const j = this.idx(c, x, y);
    return c.deckH[j] > 0 && this.moverAlt >= c.deckH[j] - WorldStream.DECK_REACH ? c.deckFlags[j] : c.roadFlags[j];
  }
  /** Height of decor (trees etc.) at tile, 0 if none. */
  decorH(x: number, y: number): number {
    const c = this.chunkAt(x, y); if (!c) return 0;
    return c.decorH[this.idx(c, x, y)];
  }
  /** How busy an area is, 0.15–1.5 (owner: "areas divided by busyness"): from the chunk's buildings and things to do — shops, eats, bars,
   *  stations, offices count double — against a downtown block's worth; an unloaded chunk answers by its zone. Cached per chunk. */
  busyAt(x: number, y: number): number {
    const cx = Math.floor(x / CHUNK), cy = Math.floor(y / CHUNK), lc = this.get(cx, cy, false);
    if (!lc) { const z = this.zoneAt(x, y); return z === 4 ? 1 : z === 2 ? 0.55 : z === 3 ? 0.5 : z === 5 ? 0.3 : 0.2; }
    const c = lc.chunk as typeof lc.chunk & { busy?: number };
    if (c.busy !== undefined) return c.busy;
    let score = 0;
    for (const b of c.buildings) { if (b.height <= 0) continue; const t = b.def.tags; score += t.some((k) => k === 'shop' || k === 'eats' || k === 'bar' || k === 'commercial' || k === 'office' || k === 'station' || k === 'mall' || k === 'club' || k === 'hotel' || k === 'bank') ? 2 : t.includes('house') || t.includes('residential') ? 0.6 : 1; }
    c.busy = Math.max(0.15, Math.min(1.5, score / 45));
    return c.busy;
  }
  zoneAt(x: number, y: number): number {
    const c = this.chunkAt(x, y); if (!c) return 1;
    return c.zone[this.idx(c, x, y)];
  }
  buildingAt(x: number, y: number): PlacedBuilding | undefined {
    const c = this.chunkAt(x, y); if (!c) return undefined;
    const fx = Math.floor(x), fy = Math.floor(y);
    return c.buildings.find((b) => fx >= b.x && fx < b.x + b.w && fy >= b.y && fy < b.y + b.h);
  }
  loaded(x: number, y: number): boolean { return this.chunkAt(x, y) !== undefined; }
  /** Buildings whose footprint comes within `r` tiles of a point (the chunk there and, near an edge, its neighbours). */
  buildingsNear(x: number, y: number, r: number): PlacedBuilding[] {
    const out: PlacedBuilding[] = []; const seen = new Set<string>();
    for (const dy of [-r, 0, r]) for (const dx of [-r, 0, r]) {
      const c = this.chunkAt(x + dx, y + dy); if (!c) continue;
      for (const b of c.buildings) { const k = `${b.x},${b.y}`; if (seen.has(k)) continue; if (x + r < b.x || x - r > b.x + b.w || y + r < b.y || y - r > b.y + b.h) continue; seen.add(k); out.push(b); }
    }
    return out;
  }

  // ---- interiors (generated floor plans, see src/world/interior.ts) ----
  /** The floor plan covering a tile on a given floor, if the building there has an interior. */
  planAt(x: number, y: number, floor = 0): FloorPlan | undefined {
    const b = this.buildingAt(x, y);
    return b ? floorPlan(b, floor, this.gen.seed) : undefined;
  }
  /** Ground-level walkers: can a person on `floor` stand on this tile of a building? undefined = no building/plan here. */
  interiorWalkable(x: number, y: number, floor = 0): boolean | undefined {
    const pl = this.planAt(x, y, floor);
    return pl ? cellWalkable(cellAt(pl, x, y)) : undefined;
  }
  /** The solid parts of a blocked interior tile in world units (thin walls — interior.ts wallSlabs); undefined = the whole tile. */
  wallRects(x: number, y: number, floor = 0): [number, number, number, number][] | undefined {
    const pl = this.planAt(x, y, floor); if (!pl) return undefined;
    const lx = Math.floor(x) - pl.x, ly = Math.floor(y) - pl.y; if (lx < 0 || ly < 0 || lx >= pl.w || ly >= pl.h) return undefined;
    const c = pl.cells[ly * pl.w + lx]; if (c === CELL_DOOR) return [];
    const slabs = wallSlabs(pl, lx, ly); if (!slabs.length) return undefined;
    const tx = Math.floor(x), ty = Math.floor(y);
    return slabs.map(([a, b, c2, d]) => [tx + a, ty + b, tx + c2, ty + d] as [number, number, number, number]);
  }
  /** Does something solid stop a projectile at (x, y, z)? Walls inside a building count; windows and floor do not. */
  solidAt(x: number, y: number, z: number): boolean {
    const bh = this.blockH(x, y);
    if (bh <= z) return false;
    const b = this.buildingAt(x, y);
    if (!b || b.height <= z) return true; // decor, or above the building
    const storyH = b.def.storyHeight ?? 3;
    const pl = floorPlan(b, Math.floor(Math.max(0, z) / storyH), this.gen.seed);
    if (!pl) return true; // sealed building
    const c = cellAt(pl, x, y);
    if (c === CELL_FURN) { const f = pl.furniture.find((q) => q.x === Math.floor(x) && q.y === Math.floor(y)); return !!f && z < Math.floor(Math.max(0, z) / storyH) * storyH + f.height; }
    return c === CELL_WALL;
  }

  // ---- destructible bits (shared by all players in multiplayer) ----
  /** tiles whose railing / street furniture / bush has been smashed, as "x,y" */
  readonly broken = new Set<string>();
  /** @deprecated alias */
  get brokenRails(): Set<string> { return this.broken; }
  /** remaining hit points of damaged decor tiles (trees, props); absent = undamaged */
  private tileHp = new Map<string, number>();
  /** Full hit points of what stands on a tile: bins and bushes go quickly, trees take a beating. 0 = nothing breakable. */
  tileMaxHp(x: number, y: number): number {
    const d = this.decorH(x, y);
    if (d <= 0 || this.blockH(x, y) > d + 1e-6) return 0; // nothing, or a building
    return d <= 1.3 ? 20 : 60 + d * 15;
  }
  /**
   * A client's world predicts, it does not decide: tile damage accumulates on the server alone and reaches the client as a
   * broken tile in the snapshot. Left on, the predicted car felled a tree the server's car was still wedged against, drove
   * off through the gap and was snapped back four tiles later (owner, Sep 11: "a rubber band stuck to the tree").
   */
  predictOnly = false;
  /** Damage what stands on a tile (bullets, explosions, ramming). Returns true when it just broke. */
  damageTile(x: number, y: number, dmg: number): boolean {
    if (this.predictOnly) return false;
    const max = this.tileMaxHp(x, y);
    if (max <= 0) return false;
    const k = WorldStream.railKey(x, y);
    const hp = (this.tileHp.get(k) ?? max) - dmg;
    if (hp > 0) { this.tileHp.set(k, hp); return false; }
    this.tileHp.delete(k);
    return this.breakTile(x, y);
  }
  /** The prop standing on a tile, if any (content def). */
  propAt(x: number, y: number): Prop | undefined {
    const tx = Math.floor(x), ty = Math.floor(y);
    const lc = this.live.get(WorldStream.key(Math.floor(tx / CHUNK), Math.floor(ty / CHUNK)));
    if (!lc) return undefined;
    const pp = lc.chunk.props.find((p) => Math.floor(p.x) === tx && Math.floor(p.y) === ty);
    return pp ? this.gen.propList[pp.prop] : undefined;
  }
  /** per chunk, the tiles with a pole on them (a lamp post, a streetlight, a camera mast) and what kind: built on first ask */
  private poles = new WeakMap<Chunk, Map<number, 'light' | 'camera'>>();
  /**
   * A pole: a tall one-tile prop that is thin — a lamp post, a park lamp, a camera mast. Too tall to push over from a
   * standstill, but it only fills the middle of its tile (a car brushes past it), and a car at speed brings it down
   * (owner, Sep 11: "lightposts are indestructible, you can get stuck in them")
   */
  pole(x: number, y: number): boolean { return this.poleKind(x, y) !== undefined; }
  /** …and which: a lamp (goes dark when it falls) or a camera mast (a sturdier thing — physics.ts CAMERA_BREAK_SPEED — and the camera stops working) */
  poleKind(x: number, y: number): 'light' | 'camera' | undefined {
    const c = this.chunkAt(x, y); if (!c) return undefined;
    let map = this.poles.get(c);
    if (!map) { map = new Map(); for (const p of c.props) { const d = this.gen.propList[p.prop]; if (d.height > CRUSH_H && d.sprite.size[0] <= 1 && d.sprite.size[1] <= 1 && (d.tags?.includes('light') || d.tags?.includes('camera'))) map.set((p.y - c.y0) * CHUNK + (p.x - c.x0), d.tags.includes('camera') ? 'camera' : 'light'); } this.poles.set(c, map); }
    const i = this.idx(c, x, y);
    const kind = map.get(i);
    return kind !== undefined && c.decorH[i] > 0 && c.blockH[i] <= c.decorH[i] + 1e-6 ? kind : undefined;
  }
  /** per chunk, the lamps that light the street at night (renderer.nightLayer): rebuilt when the chunk's props change (a lamp knocked down is gone from the list — owner, Sep 11: "do the lamp posts go dark at night if you destroy them?") */
  private lampCache = new WeakMap<Chunk, { props: PlacedProp[]; list: { x: number; y: number; h: number; sweep?: boolean }[] }>();
  lampsOf(c: Chunk): { x: number; y: number; h: number; sweep?: boolean }[] {
    const had = this.lampCache.get(c);
    if (had && had.props === c.props) return had.list;
    const list: { x: number; y: number; h: number; sweep?: boolean }[] = [];
    for (const p of c.props) { const d = this.gen.propList[p.prop]; if (d.tags?.includes('light')) list.push({ x: p.x + 0.5, y: p.y + 0.5, h: d.height, ...(d.tags.includes('sweep') ? { sweep: true } : {}) }); } // (owner, Sep 25: Area 69's searchlights sweep)
    this.lampCache.set(c, { props: c.props, list });
    return list;
  }
  /** Can a car flatten what stands on this tile? (low props and bushes, not trees or buildings) */
  /** a small thing a car can push over: a one-tile prop or decor no taller than a person and a half — a bin, a hydrant, a bench, a bush, a cactus, a small rock — never a tree (2×2 and 4 m up) or a building */
  crushable(x: number, y: number): boolean {
    const d = this.decorH(x, y);
    return d > 0 && d <= CRUSH_H && this.blockH(x, y) <= d + 1e-6;
  }
  /** Flatten a prop/bush tile: no longer blocks, ground redrawn without it. */
  breakTile(x: number, y: number): boolean {
    const k = WorldStream.railKey(x, y);
    if (this.broken.has(k)) return false;
    this.broken.add(k);
    const lc = this.live.get(WorldStream.key(Math.floor(Math.floor(x) / CHUNK), Math.floor(Math.floor(y) / CHUNK)));
    if (lc) this.applyBroken(lc, k);
    return true;
  }
  /** Apply one broken tile (or all of them) to a live chunk's data. */
  private applyBroken(lc: LiveChunk, only?: string): void {
    const c = lc.chunk;
    const keys = only ? [only] : [...this.broken];
    for (const k of keys) {
      const [x, y] = k.split(',').map(Number);
      if (x < c.x0 || y < c.y0 || x >= c.x0 + CHUNK || y >= c.y0 + CHUNK) continue;
      const i = (y - c.y0) * CHUNK + (x - c.x0);
      if (c.decorH[i] > 0 && c.blockH[i] <= c.decorH[i] + 1e-6) { c.blockH[i] = 0; c.decorH[i] = 0; }
      c.props = c.props.filter((p) => Math.floor(p.x) !== x || Math.floor(p.y) !== y);
      c.decor = c.decor.filter((d) => d.x !== x || d.y !== y);
      this.unpaint(lc);
    }
  }
  static railKey(x: number, y: number): string { return `${Math.floor(x)},${Math.floor(y)}`; }
  /** A chunk nobody is near is dropped — and with it what happened there (owner's answer Q4: the world regenerates when no live player is around). */
  private forgetChanges(c: Chunk): void {
    for (const k of this.broken) { const [x, y] = k.split(',').map(Number); if (x >= c.x0 && y >= c.y0 && x < c.x0 + CHUNK && y < c.y0 + CHUNK) this.broken.delete(k); }
    for (const k of this.ruins.keys()) { const [x, y] = k.split(',').map(Number); if (x >= c.x0 && y >= c.y0 && x < c.x0 + CHUNK && y < c.y0 + CHUNK) this.ruins.delete(k); }
    this.tileHp.forEach((_, k) => { const [x, y] = k.split(',').map(Number); if (x >= c.x0 && y >= c.y0 && x < c.x0 + CHUNK && y < c.y0 + CHUNK) this.tileHp.delete(k); });
  }

  // ---- destructible buildings: enough blast turns a building into rubble (shared like broken tiles) ----
  /** origin keys ("x,y") of collapsed buildings */
  /** ruined buildings by key → the day they fell and the height they had (the city rebuilds them after REBUILD_DAYS — `restoreBuilding`) */
  readonly ruins = new Map<string, { day: number; h: number }>();
  static readonly REBUILD_DAYS = 2;
  static readonly RUBBLE_H = 0.7;
  /** hit points of a building: footprint × storeys × 30, capped so towers are not invulnerable but take a dozen rockets */
  static buildingHp(b: PlacedBuilding): number { return Math.min(6000, Math.max(300, b.w * b.h * Math.max(1, b.def.stories) * 30)); }
  /** Apply blast damage; returns true when it collapses this call. */
  damageBuilding(b: PlacedBuilding, dmg: number, day = 0): boolean {
    if (b.ruined || b.height <= 0) return false;
    b.damage = (b.damage ?? 0) + dmg;
    if (b.damage < WorldStream.buildingHp(b)) return false;
    this.ruinBuilding(`${b.x},${b.y}`, day);
    return true;
  }
  /** Collapse by key (also used when the server says so). */
  ruinBuilding(key: string, day = 0): boolean {
    if (this.ruins.has(key)) return false;
    const [x, y] = key.split(',').map(Number);
    let h = 0; for (const lc of this.live.values()) { const b = lc.chunk.buildings.find((q) => q.x === x && q.y === y); if (b) { h = b.height; break; } }
    this.ruins.set(key, { day, h });
    for (const lc of this.live.values()) this.applyRuin(lc, x, y);
    return true;
  }
  /** The city rebuilt it: the building stands again as it was (the rubble's tiles get their height back, the damage is gone). */
  restoreBuilding(key: string): boolean {
    const r = this.ruins.get(key); if (!r) return false;
    this.ruins.delete(key);
    const [x, y] = key.split(',').map(Number);
    for (const lc of this.live.values()) {
      const c = lc.chunk, b = c.buildings.find((q) => q.x === x && q.y === y); if (!b || !b.ruined) continue;
      b.ruined = false; b.damage = 0; b.height = r.h || Math.max(3, (b.def.stories ?? 1) * 3);
      for (let ty = b.y; ty < b.y + b.h; ty++) for (let tx = b.x; tx < b.x + b.w; tx++) { if (tx < c.x0 || ty < c.y0 || tx >= c.x0 + CHUNK || ty >= c.y0 + CHUNK) continue; const i = (ty - c.y0) * CHUNK + (tx - c.x0); c.blockH[i] = b.height; c.decorH[i] = b.height; }
      clearPlans(b); this.unpaint(lc);
    }
    return true;
  }
  /** ruins older than REBUILD_DAYS on `day`: the keys the city rebuilds now */
  rebuildDue(day: number): string[] { return [...this.ruins].filter(([, r]) => day - r.day >= WorldStream.REBUILD_DAYS).map(([k]) => k); }
  private applyRuin(lc: LiveChunk, x: number, y: number): void {
    const c = lc.chunk;
    const b = c.buildings.find((q) => q.x === x && q.y === y);
    if (!b || b.ruined) return;
    b.ruined = true; b.height = WorldStream.RUBBLE_H;
    for (let ty = b.y; ty < b.y + b.h; ty++) for (let tx = b.x; tx < b.x + b.w; tx++) {
      if (tx < c.x0 || ty < c.y0 || tx >= c.x0 + CHUNK || ty >= c.y0 + CHUNK) continue;
      const i = (ty - c.y0) * CHUNK + (tx - c.x0);
      c.blockH[i] = WorldStream.RUBBLE_H; c.decorH[i] = WorldStream.RUBBLE_H; // rubble: climbable by foot, a car cannot cross
    }
    clearPlans(b);
    this.unpaint(lc);
  }
  /** Is this water tile fenced off by a bridge railing (a bridge deck is next to it)? */
  railAt(x: number, y: number): boolean {
    const t = this.terrainAt(x, y);
    if (this.deckH(x, y) > 0) return false; // (bug report, Sep 22: "car hits a ghost wall driving west to this bridge") a tile with a deck is the bridge itself: a ramp's tile ahead of a car, higher than the car, read as water beside a road — a railing across the lane, and a player's car stopped at it or "broke" it
    if (!t || t.walkable || !t.boatable || this.roadAt(x, y)) return false;
    const fx = Math.floor(x), fy = Math.floor(y);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = fx + dx, ny = fy + dy;
      const nt = this.terrainAt(nx, ny);
      if (nt && !nt.walkable && !nt.buildable && this.roadAt(nx, ny)) return true; // deck over water
    }
    return false;
  }
  railIntact(x: number, y: number): boolean { return this.railAt(x, y) && !this.broken.has(WorldStream.railKey(x, y)); }
  breakRail(x: number, y: number): boolean { return this.breakTile(x, y); }
}
